import { Dayjs } from 'dayjs';

/**
 * Locale-correct "weekday, day month" (e.g. "martes, 25 de agosto" /
 * "Tuesday, August 25") via Intl.DateTimeFormat rather than dayjs's own
 * .format() -- dayjs's locale is never switched to match the app's i18n
 * language (see LanguageSwitcher, which only calls i18n.changeLanguage),
 * so a dayjs format string always renders weekday/month names in English
 * regardless of the UI language. Intl.DateTimeFormat instead takes the
 * locale as an explicit argument every call, and already knows each
 * locale's own weekday/day/month ordering and connecting words (Spanish's
 * "de" included) -- no hardcoded literal needed.
 */
export const formatFullDate = (date: Dayjs, language: string): string =>
  new Intl.DateTimeFormat(language, { weekday: 'long', day: 'numeric', month: 'long' }).format(date.toDate());

/**
 * Compact "25 ago, 14:30" for timestamps sitting next to a label (the
 * booking timeline's own phase rows) where the weekday and full month name
 * of formatFullDate would crowd the line out on a phone. Same
 * Intl-over-dayjs reasoning as above -- see that function's own doc comment.
 * Takes an ISO string rather than a Dayjs since every caller so far reads
 * these straight off the API response.
 */
export const formatShortDateTime = (isoDate: string, language: string): string =>
  new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(isoDate));
